import { useQuery } from '@apollo/client';
import { Box, Icon, Spinner, Text, ToastAction, ToastType, useToastsDispatch } from '@bechtel/gds';
import {
  OperatorType,
  RequisitionCatalogToolRoom,
  RequisitionToolCatalog,
  SearchInput,
  SearchRequisitionToolCatalogResult,
  ToolRoom,
} from '@bechtel/ht-types';
import { filter, find, flatMap, get, map, sumBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { Backdrop } from '~/components/backdrop';
import { REQUISITION_TOOL_CATALOG_COUNT_FOR_SEARCH, SEARCH_REQUISITION_TOOL_CATALOGS } from '~/gql';
import { useGetToolRooms } from '~/hooks';
import { PrimaryButtonStyled } from '~/styles';
import { mediaServiceBasePaths } from '~/types/common';
import axiosInstance from '~/utilities/axiosInstance';
import { CatalogItem } from '../../active-requisition/catalog-item';
import { StickyColumnStyled } from '../../active-requisition/styled';
import { ReqDetailFooterStyled, RequisitionDetailsWrapper, TableStyledExt } from './styled';

const RequisitionDetails = () => {
  const pageLimit = 20;
  const { requisitionId } = useParams();
  const { t } = useTranslation();
  const { getToolRoomList, data: toolRoomsData, loading: toolRoomsLoading } = useGetToolRooms(true);
  const toolRooms: ToolRoom[] = get(toolRoomsData, 'searchToolRooms.items', []);
  const theme = useTheme();
  const [download, setDownload] = useState(false);
  const dispatchToast = useToastsDispatch();

  const { data, loading, fetchMore } = useQuery<
    { searchRequisitionToolCatalogs: SearchRequisitionToolCatalogResult },
    { searchInput: SearchInput }
  >(SEARCH_REQUISITION_TOOL_CATALOGS, {
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              operator: OperatorType.EQUAL,
              value: requisitionId,
              field: 'requisitionId',
            },
          ],
        },
        sortInput: [],
        paginationInput: {
          offset: 0,
          limit: pageLimit,
        },
      },
    },
  });

  const { loading: totalReqCatalogsCountLoading, data: totalReqCatalogsCount } = useQuery<
    {
      requisitionToolCatalogCount: number;
    },
    { searchInput: SearchInput }
  >(REQUISITION_TOOL_CATALOG_COUNT_FOR_SEARCH, {
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              operator: OperatorType.EQUAL,
              value: requisitionId,
              field: 'requisitionId',
            },
          ],
        },
        sortInput: [],
      },
    },
  });

  const reqToolCatalogs: RequisitionToolCatalog[] = get(data, 'searchRequisitionToolCatalogs.items', []);
  const reqCatalogToolRooms: RequisitionCatalogToolRoom[] = flatMap(reqToolCatalogs, 'requisitionCatalogToolRooms');

  const handleScroll = (e) => {
    if (!totalReqCatalogsCount?.requisitionToolCatalogCount) return;

    if (
      e.target.scrollTop + e.target.clientHeight + 50 >= e.target.scrollHeight &&
      !loading &&
      !totalReqCatalogsCountLoading &&
      reqToolCatalogs?.length < totalReqCatalogsCount?.requisitionToolCatalogCount
    ) {
      fetchMore({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  operator: OperatorType.EQUAL,
                  value: requisitionId,
                  field: 'requisitionId',
                },
              ],
            },
            paginationInput: {
              limit: pageLimit,
              offset: reqToolCatalogs.length,
            },
            sortInput: [],
          },
        },
      });
    }
  };

  const getTotalQtyByToolRoom = (toolRoomId: string) => {
    const filteredToolRooms = filter(
      reqCatalogToolRooms,
      (toolRoom: RequisitionCatalogToolRoom) => toolRoom.toolRoomId === toolRoomId
    );
    return sumBy(filteredToolRooms, (item: RequisitionCatalogToolRoom) => item.quantityOrdered ?? 0);
  };

  const downloadFile = async () => {
    setDownload(true);
    const uploadErrorToast: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.DANGER,
        content: t('CSV_DOWNLOAD_ERROR'),
        autoDismissTimeout: 3000,
      },
    };
    try {
      axiosInstance
        .get(`${mediaServiceBasePaths.requisitionExport}/${requisitionId}`, {
          responseType: 'blob',
        })
        .then((response) => {
          setDownload(false);
          if (!response) {
            dispatchToast(uploadErrorToast);
            return;
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = `Requisition.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          setDownload(false);
          dispatchToast(uploadErrorToast);
        });
    } catch (error) {
      setDownload(false);
      dispatchToast(uploadErrorToast);
    }
  };

  useEffect(() => {
    getToolRoomList({
      filterInput: {
        and: [
          {
            field: 'isActive',
            operator: OperatorType.EQUAL,
            value: 'true',
          },
        ],
      },
      sortInput: [],
    });
  }, []);

  return (
    <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
      {(loading || toolRoomsLoading || totalReqCatalogsCountLoading) && <Backdrop />}
      <RequisitionDetailsWrapper onScroll={handleScroll}>
        <TableStyledExt>
          <thead>
            <tr>
              <th style={{ minWidth: '340px', zIndex: 1 }}>{t('ITEM_DESCRIPTION')}</th>
              {toolRooms?.length > 0 &&
                map(toolRooms, (toolRoom) => {
                  return (
                    getTotalQtyByToolRoom(toolRoom.id) > 0 && (
                      <th key={`th-${toolRoom?.id}`} style={{ minWidth: '160px' }}>
                        <Box
                          justifyContent={{ sm: 'space-between' }}
                          alignItems={{ sm: 'center' }}
                          css={{ lineHeight: 1.2 }}
                        >
                          <span>{toolRoom?.name}</span>
                        </Box>
                      </th>
                    )
                  );
                })}
              <th style={{ minWidth: '120px' }}>{t('TOTAL_REQUESTED')}</th>
            </tr>
          </thead>
          <tbody>
            {reqToolCatalogs &&
              reqToolCatalogs.length > 0 &&
              map(reqToolCatalogs, (reqToolCatalog) => {
                const allTRSum = sumBy(reqToolCatalog.requisitionCatalogToolRooms, 'quantityOrdered');
                return (
                  <tr key={`tr-${reqToolCatalog?.id}`}>
                    <StickyColumnStyled>
                      <CatalogItem
                        catalogName={reqToolCatalog.toolCatalog?.name || ''}
                        uom={reqToolCatalog?.toolCatalog?.uom}
                        bscnPartNumber={reqToolCatalog?.toolCatalog?.bscnPartNumber || ''}
                        toolCatalogImageUrl={reqToolCatalog?.toolCatalog?.toolCatalogImageUrl}
                        toolCatalogThumbnailImageUrl={reqToolCatalog?.toolCatalog?.toolCatalogThumbnailImageUrl}
                        fallbackName={reqToolCatalog?.toolCatalog?.toolCategory?.name}
                      />
                    </StickyColumnStyled>
                    {toolRooms.length > 0 &&
                      map(toolRooms, (toolRoom) => {
                        const qty =
                          find(
                            reqToolCatalog.requisitionCatalogToolRooms,
                            (catalogToolRoom: RequisitionCatalogToolRoom) => catalogToolRoom.toolRoomId === toolRoom.id
                          )?.quantityOrdered ?? 0;
                        return (
                          getTotalQtyByToolRoom(toolRoom.id) > 0 && (
                            <td key={`td-${toolRoom?.id}`}>
                              <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
                                {qty}
                              </Box>
                            </td>
                          )
                        );
                      })}
                    <td style={{ background: theme.colors.grey[50] }}>{allTRSum}</td>
                  </tr>
                );
              })}
          </tbody>
          <tfoot>
            <tr>
              <StickyColumnStyled>{totalReqCatalogsCount?.requisitionToolCatalogCount ?? 0}</StickyColumnStyled>
              {toolRooms.length > 0 &&
                map(toolRooms, (toolRoom) => {
                  const totalQty = getTotalQtyByToolRoom(toolRoom.id);
                  return (
                    totalQty > 0 && (
                      <td key={`tf-${toolRoom?.id}`}>
                        <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
                          {totalQty}
                        </Box>
                      </td>
                    )
                  );
                })}
              <td>{sumBy(reqCatalogToolRooms, 'quantityOrdered')}</td>
            </tr>
          </tfoot>
        </TableStyledExt>
      </RequisitionDetailsWrapper>
      <ReqDetailFooterStyled>
        <PrimaryButtonStyled type="button" onClick={downloadFile} disabled={download}>
          <Box gap={{ sm: '400' }} px={{ sm: '400' }}>
            {download ? <Spinner /> : <Icon icon="download"></Icon>}
            <Text color="white" size="300">
              {t('Download')}
            </Text>
          </Box>
        </PrimaryButtonStyled>
      </ReqDetailFooterStyled>
    </Box>
  );
};

export default RequisitionDetails;
